import { DollarSign, Eye, RefreshCw, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import moment from "moment";

import ActionMenu from "../../../components/shared/ActionMenu";
import CurrencyDisplay from "../../../components/shared/CurrencyDisplay";
import DataTable from "../../../components/shared/DataTable";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";

const CostingTable = ({
  costings,
  onEdit,
  onDelete,
  onViewImage,
  onRefresh,
  permissions,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setCostingPage
}) => {
  const columns = [
    {
      header: "Contractor",
      accessor: "contractor",
      isPrimary: true,
      render: (value, costing) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-yale-blue-900 dark:text-slate-100">{value?.contractorName || "N/A"}</span>
          {costing.deleted && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
              Deleted
            </span>
          )}
        </div>
      )
    },
    {
      header: "Type",
      accessor: "contractor",
      render: (value) => <span className="text-xs text-slate-500">{value?.type || "N/A"}</span>
    },
    {
      header: "Project",
      accessor: "project",
      render: (value) => (
        <div className="text-xs font-semibold text-yale-blue-700 dark:text-yale-blue-400">{value?.name || "N/A"}</div>
      )
    },
    {
      header: "Date",
      accessor: "costingDate",
      sortable: true,
      sortKey: "costingDate",
      render: (value) => moment(value).format("DD/MM/YYYY")
    },
    {
      header: "Est. Amt",
      accessor: "estimatedAmount",
      render: (value) => (
        <span className="font-mono text-sm text-green-600 dark:text-green-400">
          <CurrencyDisplay amount={value} />
        </span>
      )
    },
    {
      header: "Consumed Amt",
      accessor: "consumedAmount",
      render: (value) => (
        <span className="font-mono text-sm text-orange-600 dark:text-orange-400">
          <CurrencyDisplay amount={value} />
        </span>
      )
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCostingPage(0);
                }}
                id="show-deleted-costing-table"
              />
              <Label htmlFor="show-deleted-costing-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      cellClassName: "text-right",
      render: (_, costing) => {
        const hasView = costing.documents && costing.documents.length > 0;
        const customActions = (
          <>
            {hasView && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onViewImage(costing.documents);
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Eye className="w-4 h-4 text-blue-500" />
                <span>View Attachments</span>
              </DropdownMenuItem>
            )}
            {!costing.deleted && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh(costing.id);
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4 text-green-500" />
                <span>Refresh Consumed Amt</span>
              </DropdownMenuItem>
            )}
          </>
        );

        return (
          <ActionMenu
            onEdit={() => onEdit(costing)}
            onDelete={() => onDelete(costing.id)}
            canEdit={permissions?.canUpdate && !costing.deleted}
            canDelete={permissions?.canDelete && !costing.deleted}
            customActions={customActions}
          />
        );
      }
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={costings}
      emptyStateTitle="No costing records found"
      emptyStateDescription="Add a new costing record to track project expenses."
      emptyStateIcon={DollarSign}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default CostingTable;
